import React, { useCallback, useMemo, useRef, useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import debounce from 'lodash/debounce';
import { WithSnackbarProps, withSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ConfirmButton from '~/components/Common/Elements/ConfirmButton';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import cleanProperty from '~/components/Vendors/Properties/helpers/cleanProperty';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import useAppNavigation from '~/hooks/useAppNavigation';

import BedbankService from '~/services/BedbankService';
import ReservationService from '~/services/ReservationService';
import { formatDateISO } from '~/services/TimeService';

import deleteNulls from '~/utils/deleteNulls';
import featureToggle from '~/utils/featureToggle';
import { reportError } from '~/utils/reportError';

import { FlatImageEditorPendingUploadState } from '../Images/types';
import AsyncAutocomplete from '../Legacy/AsyncAutocomplete';
import PropertyMap from '../PropertyMap';

import SingleImageUploadField, { SingleImageUploadFieldHandle } from './SingleImageUploadField';
import { buttonMessages, buttonStates } from './states/submitButton';

const uiSchema: UiSchema = {
  id_salesforce_external: { 'ui:widget': 'hidden' },
  'ui:order': [
    '*',
    'parent_property_id',
    'bedbank_property_id',
    'logo_id_cloudinary_external',
    'auto_luxplus_margin_threshold',
    'auto_luxplus_max_discount',
    'auto_luxplus_max_nightly_discount',
    'auto_luxplus_min_discount',
    'geo_data',
  ],
  max_child_age: {
    'ui:help': 'The age up to which a child is classified as a child',
  },
  max_infant_age: {
    'ui:help': 'The age up to which a child is classified as an infant',
  },
  max_teenager_age: {
    'ui:widget': (props) => (
      <Box>
        <FormControlLabel
          label="Enable Teenager Age Limit"
          control={
            <Checkbox
              checked={props.value !== null && props.value !== '' && props.value !== undefined}
              onChange={(e) => {
                props.onChange(e.target.checked ? 17 : null);
              }}
            />
          }
        />
        {props.value !== null && props.value !== '' && props.value !== undefined && (
          <TextField
            label="Max age of Teenagers"
            type="number"
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            fullWidth
            margin="normal"
            helperText="The age up to which a child is classified as a teenager"
          />
        )}
      </Box>
    ),
  },
  geo_data: {
    'ui:classNames': 'geo-field',
  },
  regions_marketed_los: {
    'ui:options': {
      addable: false,
      orderable: false,
      removable: false,
    },
  },
  location_heading: {
    'ui:title': 'Location City',
  },
  location_subheading: {
    'ui:title': 'Location Country/State',
  },
};

const guidReg = new RegExp('\\b[0-9a-f]{8}\\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\\b[0-9a-f]{12}\\b');

const marginSharingFieldKeys = [
  'auto_luxplus_margin_threshold',
  'auto_luxplus_min_discount',
  'auto_luxplus_max_discount',
  'auto_luxplus_max_nightly_discount',
];
const marginSharingIntegerKeys = [
  'auto_luxplus_min_discount',
  'auto_luxplus_max_discount',
  'auto_luxplus_max_nightly_discount',
];

const MARGIN_SHARING_LABELS: Record<string, string> = {
  auto_luxplus_margin_threshold: 'LPC Auto Luxplus Margin threshold %',
  auto_luxplus_max_discount: 'LPC Auto Luxplus max discount (0–300)',
  auto_luxplus_max_nightly_discount: 'LPC Auto Luxplus max nightly discount (0–100)',
  auto_luxplus_min_discount: 'LPC Auto Luxplus min discount (0–100)',
};

const MARGIN_THRESHOLD_KEY = 'auto_luxplus_margin_threshold';

const marginSharingOrder: Array<keyof typeof MARGIN_SHARING_LABELS> = [
  'auto_luxplus_margin_threshold',
  'auto_luxplus_max_discount',
  'auto_luxplus_max_nightly_discount',
  'auto_luxplus_min_discount',
];

function marginSharingFromProperty(p: Record<string, unknown>): Record<string, number | null> {
  return Object.fromEntries(
    marginSharingFieldKeys.map((k) => {
      const v = p[k];
      if (v === undefined || v === '' || v === null) {
        return [k, null];
      }
      const n = Number(v);
      return [k, Number.isNaN(n) ? null : n];
    }),
  ) as Record<string, number | null>;
}

const propertyKeysToDeleteIfNull = ['hotel_meta_provider', 'pms', 'max_teenager_age'];

const contentEnabled = featureToggle.availableToShow('ADDITIONAL_PROPERTY_CONTENT_ENABLED');

function formExtraValidation(formData, errors) {
  if (formData.max_child_age <= formData.max_infant_age) {
    errors.max_child_age.addError('Max age of Children must be higher than Max age of Infants');
  }

  if (!Number.isInteger(formData.max_child_age)) {
    errors.max_child_age.addError('Max age of Children must be integer');
  }

  if (
    formData.max_teenager_age !== null &&
    formData.max_teenager_age !== undefined &&
    !Number.isInteger(formData.max_teenager_age)
  ) {
    errors.max_teenager_age.addError('Max age of Teenagers must be integer');
  }

  return errors;
}

type Props = WithSnackbarProps & {
  disabled?: boolean;
  formId?: string;
  hideActions?: boolean;
  onDraftChange?: (property: Record<string, unknown>) => void;
  onSubmitError?: () => void;
  onSubmitStart?: () => void;
  onSubmitSuccess?: (property: App.Property) => void;
  property: unknown;
  schema: RJSFSchema;
  vendorId: string;
};

export function PropertyForm({
  disabled = false,
  formId,
  hideActions,
  onDraftChange,
  onSubmitError,
  onSubmitStart,
  onSubmitSuccess,
  property: initialProperty,
  schema: initialSchema,
  vendorId,
  enqueueSnackbar,
}: Props) {
  const { push } = useAppNavigation();

  const [property, setProperty] = useState(cleanProperty(deleteNulls(initialProperty, propertyKeysToDeleteIfNull)));
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [errorMsg, setErrorMsg] = useState('');
  const [bedbankPropertyInvalid, setBedbankPropertyInvalid] = useState(false);
  const [logoUploadState, setLogoUploadState] = useState<FlatImageEditorPendingUploadState>({
    isUploadingPending: false,
    pendingUploadCount: 0,
  });
  const logoFieldRef = useRef<SingleImageUploadFieldHandle | null>(null);

  const [marginSharingLocal, setMarginSharingLocal] = useState<Record<string, number | null>>(() =>
    marginSharingFromProperty((initialProperty || {}) as Record<string, unknown>),
  );

  const isLpcProperty = property?.channel_manager !== PROPERTY_CHANNEL_MANAGERS.selfManaged;

  // Create a copy of the schema to modify
  const schema = useMemo(() => {
    const schemaCopy = { ...initialSchema, properties: { ...initialSchema.properties } };
    delete schemaCopy.properties.taxes_and_fees_content;
    delete schemaCopy.properties.commissionable_taxes_and_fees;
    delete schemaCopy.properties.taxes_and_fees;
    // Allow string so empty inputs ("") and numeric strings pass client-side validation
    for (const key of marginSharingFieldKeys) {
      const prop = schemaCopy.properties?.[key];
      if (prop && typeof prop === 'object' && Array.isArray(prop.type)) {
        const types = prop.type as Array<string>;
        const typeWithString = [...types, 'string'] as RJSFSchema['type'];
        schemaCopy.properties[key] = { ...prop, type: typeWithString };
      }
    }
    return schemaCopy;
  }, [initialSchema]);

  const debouncedSetProperty = useMemo(
    () =>
      debounce((newProperty) => {
        setProperty(newProperty);
      }, 150),
    [],
  );

  const onSubmit = async (form: IChangeEvent) => {
    form.errors = [];
    form.errorSchema = {};

    setSaveState(buttonStates.saving);
    onSubmitStart?.();
    let logoUploadResult;

    try {
      logoUploadResult = await logoFieldRef.current?.uploadAllPending();
    } catch {
      setSaveState(buttonStates.failed);
      onSubmitError?.();
      return;
    }

    if (logoUploadResult?.failedCount) {
      setSaveState(buttonStates.failed);
      onSubmitError?.();
      return;
    }

    const submittedFormData = {
      ...form.formData,
      logo_id_cloudinary_external: logoFieldRef.current?.getValue() ?? form.formData.logo_id_cloudinary_external ?? '',
    };

    setProperty({ ...submittedFormData, ...(isLpcProperty ? marginSharingLocal : {}) });

    let submitType = 'createProperty';
    let id = null;
    if (property.id) {
      submitType = 'updateProperty';
      id = property.id;
    }

    if (submittedFormData.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft) {
      delete submittedFormData.channel_ari_type;
      delete submittedFormData.channel_rate_type;
      delete submittedFormData.channel_rate_type_flash_override;
      delete submittedFormData.send_nightly_reservation_rates;
      delete submittedFormData.generate_infant_capacities_from_children;
    }

    if (submittedFormData.channel_manager === PROPERTY_CHANNEL_MANAGERS.selfManaged) {
      submittedFormData.regions_marketed_los = [];
    }

    // Don't send large room_types data as it is not on this form
    const formDataWithoutRoomTypes = { ...submittedFormData };
    if ('room_types' in formDataWithoutRoomTypes) {
      delete formDataWithoutRoomTypes['room_types'];
    }

    if (isLpcProperty) {
      for (const key of marginSharingFieldKeys) {
        formDataWithoutRoomTypes[key] = marginSharingLocal[key] ?? null;
      }
    }

    // Fallback in case we do not touch the field in the form
    if (
      formDataWithoutRoomTypes.margin_sharing_enabled === null ||
      formDataWithoutRoomTypes.margin_sharing_enabled === undefined
    ) {
      formDataWithoutRoomTypes.margin_sharing_enabled = false;
    }

    ReservationService[submitType](formDataWithoutRoomTypes, id)
      .then((response) => {
        setSaveState(buttonStates.default);
        enqueueSnackbar('Saved successfully', { variant: 'success' });

        if (onSubmitSuccess) {
          onSubmitSuccess(response as App.Property);
          return;
        }

        push(`/vendors/${vendorId}/properties/${response.id}`);
      })
      .catch((e) => {
        onSubmitError?.();

        if (e.name === 'ValidationError' && e.errors[0]?.message === 'bedbank_property_id must be unique') {
          setBedbankPropertyInvalid(true);
          setSaveState(buttonStates.failed);
        } else {
          setSaveState(buttonStates.failed);
          reportError(e);
        }
      });
  };

  const handleUiChanges = useCallback((newProperty, currentProperty) => {
    if (!newProperty.hotel_meta_provider && !!currentProperty.hotel_meta_code) {
      newProperty.hotel_meta_code = '';
    }
    if (
      (!newProperty.channel_manager || newProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.selfManaged) &&
      !!currentProperty.hotel_code
    ) {
      newProperty.hotel_code = '';
    }
    if (newProperty.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft && !!currentProperty.channel_supplier_id) {
      newProperty.channel_supplier_id = '';
    }
    if (newProperty.channel_manager !== PROPERTY_CHANNEL_MANAGERS.travelclick) {
      delete newProperty.pms;
    }
    if (newProperty.channel_manager === PROPERTY_CHANNEL_MANAGERS.travelclick && !newProperty.pms) {
      delete newProperty.pms;
    }
  }, []);

  const onChange = useCallback(
    (edit: IChangeEvent) => {
      const newProperty = edit.formData;

      if (saveState !== buttonStates.default) {
        setSaveState(buttonStates.default);
      }

      const hasUiChanges =
        newProperty.channel_manager !== property.channel_manager ||
        newProperty.hotel_meta_provider !== property.hotel_meta_provider;

      if (hasUiChanges) {
        handleUiChanges(newProperty, property);
      }

      if (hideActions) {
        setProperty(newProperty);
        onDraftChange?.(newProperty);
        return;
      }

      if (hasUiChanges) {
        setProperty(newProperty);
      } else {
        debouncedSetProperty(newProperty);
      }
    },
    [saveState, debouncedSetProperty, property, handleUiChanges, hideActions, onDraftChange],
  );

  const onDelete = () => {
    ReservationService.deleteProperty(property.id)
      .then(() => {
        push(`/vendors/${vendorId}`);
      })
      .catch((e) => {
        reportError(e);
      });
  };

  const onRestore = () => {
    ReservationService.restoreProperty(property.id)
      .then(() => {
        push(`/vendors/${vendorId}`);
      })
      .catch((e) => {
        reportError(e);
      });
  };

  const onMapClick = (latitude: string, longitude: string) => {
    // TODO: save the place ID (future enhancement)

    const edit = {
      formData: Object.assign({}, property, {
        latitude,
        longitude,
      }),
    };
    onChange(edit as IChangeEvent);
  };

  const onReverseGeocode = (result) => {
    const edit = {
      formData: Object.assign({}, property, {
        geo_data: result,
      }),
    };
    onChange(edit as IChangeEvent);
  };

  const getParentPropertyPredictions = (input, callback) => {
    if (!input) {
      return callback([]);
    }
    if (guidReg.test(input)) {
      ReservationService.getProperty(input).then((prediction) => {
        if (!prediction || prediction.result.parent_property_id) {
          return callback([]);
        }
        return callback([prediction.result]);
      });
    } else {
      const limit = 100;
      const page = 1;
      const includeDeleted = false;
      ReservationService.getPropertiesByName(input, limit, page, includeDeleted).then((predictions) => {
        if (!predictions) {
          return callback([]);
        }
        const filtered = predictions.result.filter((property) => !property.parent_property_id);
        return callback(filtered);
      });
    }
  };

  const debouncedGetParentPredictions = debounce(getParentPropertyPredictions, 500);

  const onChangeWithParentProperty = (selected) => {
    if (selected) {
      setProperty({
        ...property,
        parent_property_id: selected.id,
        parentProperty: selected,
      });
    }
  };

  const clearParentProperty = () => {
    const newProperty = { ...property };
    delete newProperty.parent_property_id;
    delete newProperty.parentProperty;
    setProperty(newProperty);
  };

  const getBedbankPropertyPredictions = (input: string, callback) => {
    if (!input) {
      return callback([]);
    }

    const params = {
      query: input,
      type: BedbankService.isBedbankId(input) ? 'id' : 'name',
    };

    BedbankService.getProperties(1, 20, params)
      .then((predictions) => {
        if (!predictions) {
          return callback([]);
        }
        return callback(predictions.result);
      })
      .catch((error) => {
        enqueueSnackbar(error.message || 'Error! Unable to fetch properties.', {
          variant: 'error',
        });
      });
  };

  const debouncedGetBedbankPredictions = debounce(getBedbankPropertyPredictions, 500);

  const onChangeWithBedbankProperty = (selected) => {
    if (selected) {
      setBedbankPropertyInvalid(false);
      setProperty({
        ...property,
        bedbank_property_id: selected.id,
        bedbankProperty: selected,
      });
    }
  };

  const clearBedbankProperty = () => {
    const newProperty = { ...property };
    delete newProperty.bedbank_property_id;
    delete newProperty.bedbankProperty;
    setProperty(newProperty);
  };

  const mapParentPropertyLabel = (property) => {
    if (!property) {
      return '';
    }

    return `
      ${property.name} | Vendor: ${property.id_salesforce_external}
      ${property.channel_manager ? ` | CM: ${property.channel_manager}` : ''}
      ${property.hotel_code ? ` | Hotel Code: ${property.hotel_code}` : ''}
      | Created: ${formatDateISO(property.created_at)}`;
  };

  let deleteButton = null;
  let restoreButton = null;

  if (property.id) {
    if (property.deleted_at) {
      restoreButton = (
        <ConfirmButton confirmTitle="Restore property" confirmAnswer="Yes, continue" onConfirm={onRestore}>
          Restore Property
        </ConfirmButton>
      );
    } else {
      deleteButton = (
        <ConfirmButton
          confirmTitle="Delete property"
          confirmQuestion="Are you sure you want to delete this property?"
          confirmAnswer="Yes, delete"
          onConfirm={onDelete}
        >
          Delete
        </ConfirmButton>
      );
    }
  }

  const formProperty = Object.assign({}, property);

  const propertyData = property as Record<string, unknown>;
  for (const key of marginSharingFieldKeys) {
    const v = propertyData[key];
    formProperty[key] = v === undefined || v === '' ? null : v;
  }

  if (typeof formProperty.latitude !== 'number' && typeof formProperty.latitude !== 'string') {
    delete formProperty.latitude;
  }

  if (typeof formProperty.longitude !== 'number' && typeof formProperty.longitude !== 'string') {
    delete formProperty.longitude;
  }

  if (!formProperty.logo_id_cloudinary_external) {
    delete formProperty.logo_id_cloudinary_external;
  }

  const currentUiSchema = { ...uiSchema };

  currentUiSchema.parent_property_id = {
    'ui:title': 'Parent Property',
    'ui:widget': (props) => (
      <>
        {props.registry.formContext?.parentProperty && (
          <Stack mb={1} direction="row" spacing={2} alignItems="center">
            <Typography>{props.registry.formContext.parentProperty.name}</Typography>
            <IconButton aria-label="Remove" onClick={clearParentProperty}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )}

        <AsyncAutocomplete
          loadOptions={debouncedGetParentPredictions}
          onChange={onChangeWithParentProperty}
          getOptionLabel={mapParentPropertyLabel}
          placeholder="Search for a property by name or hotel code"
          label="Parent property"
        />
      </>
    ),
  };

  currentUiSchema.bedbank_property_id = {
    'ui:title': 'Bedbank Property',
    'ui:widget': (props) => (
      <>
        {props.registry.formContext?.bedbankProperty && (
          <Stack mb={1} direction="row" spacing={2} alignItems="center">
            <Typography>{props.registry.formContext.bedbankProperty.name}</Typography>
            <IconButton aria-label="Remove" onClick={clearBedbankProperty}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )}
        {bedbankPropertyInvalid && (
          <Alert severity="warning">This property is already linked to another property</Alert>
        )}

        <AsyncAutocomplete
          loadOptions={debouncedGetBedbankPredictions}
          onChange={onChangeWithBedbankProperty}
          getOptionLabel={(property: App.Property) => property.name}
          placeholder="Search for a property by name or hotel code"
          label="Bedbank property"
        />
      </>
    ),
  };

  currentUiSchema.hotel_meta_code = {
    'ui:widget': property.hotel_meta_provider ? 'text' : 'hidden',
  };

  currentUiSchema.hotel_code = {
    'ui:widget':
      property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.selfManaged && !!property.channel_manager
        ? 'text'
        : 'hidden',
  };

  currentUiSchema.channel_supplier_id = {
    'ui:widget': property.channel_manager === PROPERTY_CHANNEL_MANAGERS.derbysoft ? 'text' : 'hidden',
  };

  currentUiSchema.logo_id_cloudinary_external = {
    'ui:title': 'Property logo',
    'ui:widget': (props) => (
      <SingleImageUploadField
        ref={logoFieldRef}
        value={props.value}
        uploadHelperText="Crop and adjust the logo before uploading it."
        onChange={props.onChange}
        onPendingUploadStateChange={setLogoUploadState}
      />
    ),
  };

  if (property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.derbysoft) {
    currentUiSchema.channel_ari_type = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.channel_rate_type = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.channel_rate_type_flash_override = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.send_nightly_reservation_rates = {
      'ui:widget': 'hidden',
    };
    currentUiSchema.generate_infant_capacities_from_children = {
      'ui:widget': 'hidden',
    };
  } else {
    currentUiSchema.channel_ari_type = schema.properties.channel_ari_type;
    currentUiSchema.channel_rate_type = schema.properties.channel_rate_type;
    currentUiSchema.channel_rate_type_flash_override = schema.properties.channel_rate_type_flash_override;
    currentUiSchema.send_nightly_reservation_rates = schema.properties.send_nightly_reservation_rates;
    currentUiSchema.generate_infant_capacities_from_children =
      schema.properties.generate_infant_capacities_from_children;
  }

  if (property.channel_manager !== PROPERTY_CHANNEL_MANAGERS.travelclick) {
    currentUiSchema.pms = {
      'ui:widget': 'hidden',
    };
  } else {
    currentUiSchema.pms = schema.properties.pms;
  }

  if (!contentEnabled) {
    currentUiSchema.videos = {
      'ui:widget': 'hidden',
    };
  }

  /**
   * Needed to hide the margin sharing fields displayed automatically from schema
   */
  marginSharingFieldKeys.forEach((key) => {
    currentUiSchema[key] = { 'ui:widget': 'hidden' };
  });
  currentUiSchema.margin_sharing_enabled = { 'ui:widget': 'hidden' };

  const handleMarginSharingChange = useCallback((key: string, value: string) => {
    if (value === '') {
      setMarginSharingLocal((prev) => ({ ...prev, [key]: null }));
      return;
    }
    if (key === MARGIN_THRESHOLD_KEY) {
      const pct = parseFloat(value);
      const next = Number.isNaN(pct) ? null : Math.min(100, Math.max(0, pct)) / 100;
      setMarginSharingLocal((prev) => ({ ...prev, [key]: next }));
      return;
    }
    const parsed = marginSharingIntegerKeys.includes(key) ? parseInt(value, 10) : parseFloat(value);
    const next = Number.isNaN(parsed) ? null : parsed;
    setMarginSharingLocal((prev) => ({ ...prev, [key]: next }));
  }, []);

  return (
    <Form
      id={formId}
      schema={schema}
      onChange={onChange}
      formData={formProperty}
      onSubmit={onSubmit}
      uiSchema={currentUiSchema}
      formContext={formProperty}
      customValidate={formExtraValidation}
      disabled={disabled}
      validator={validator}
    >
      {isLpcProperty && (
        <Box mt={3} mb={2}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            LPC Auto Luxplus Margin sharing Config
          </Typography>
          <Stack spacing={2}>
            {marginSharingOrder.map((key) => {
              const isThreshold = key === MARGIN_THRESHOLD_KEY;
              const raw = marginSharingLocal[key];
              const displayValue = isThreshold && raw != null ? Math.round(Number(raw) * 100) : (raw ?? '');
              return (
                <TextField
                  key={key}
                  label={MARGIN_SHARING_LABELS[key]}
                  type="number"
                  value={displayValue}
                  onChange={(e) => handleMarginSharingChange(key, e.target.value)}
                  placeholder={isThreshold ? 'Optional (0–100)' : 'Optional'}
                  disabled={disabled}
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      max: isThreshold ? 100 : key === 'auto_luxplus_max_discount' ? 300 : 100,
                    },
                  }}
                />
              );
            })}
            <FormControlLabel
              control={
                <Checkbox
                  checked={property.margin_sharing_enabled === true}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange({
                      formData: { ...formProperty, margin_sharing_enabled: e.target.checked },
                    } as IChangeEvent)
                  }
                />
              }
              label="LPC Auto Luxplus Margin sharing enabled"
            />
          </Stack>
        </Box>
      )}

      <Box
        mt={2}
        aria-disabled={disabled || undefined}
        inert={disabled || undefined}
        sx={{ pointerEvents: disabled ? 'none' : undefined, opacity: disabled ? 0.6 : undefined }}
      >
        <label>Select location</label>
        <PropertyMap
          name={formProperty.name}
          address={formProperty.address}
          lat={formProperty.latitude}
          lng={formProperty.longitude}
          onMapClick={onMapClick}
          reverseGeocode
          onReverseGeocode={onReverseGeocode}
          setErrorMsg={setErrorMsg}
        />
      </Box>

      {errorMsg && (
        <Box mt={2}>
          <ErrorDisplay message={errorMsg} />
        </Box>
      )}

      {!hideActions && (
        <Box className="button-container">
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              className={saveState}
              disabled={disabled || logoUploadState.isUploadingPending}
              variant="contained"
            >
              {buttonMessages[saveState]}
            </Button>
            {restoreButton ? restoreButton : deleteButton}
          </Stack>
        </Box>
      )}
    </Form>
  );
}

export default withSnackbar(PropertyForm);
